import React, { useState } from "react";
import { X, Percent, Euro } from "lucide-react";
import NumericKeypad from "./NumericKeypad";

/**
 * Modale Sconto — applicabile all'intero conto o a una riga specifica.
 * Props:
 *  - open, onClose
 *  - target: { type: 'order'|'line', label, currentTotal }  // per anteprima
 *  - onConfirm({ mode: 'pct'|'amount', value })
 */
export default function DiscountModal({ open, onClose, onConfirm, target }) {
  const [mode, setMode] = useState("pct");
  const [val, setVal] = useState("");

  if (!open) return null;

  const num = parseFloat(val || 0) || 0;
  const total = parseFloat(target?.currentTotal || 0) || 0;
  const preview = mode === "pct"
    ? Math.max(0, total * (1 - num / 100))
    : Math.max(0, total - num);
  const canConfirm = num > 0 && (mode === "pct" ? num <= 100 : num <= total);

  const submit = () => {
    if (!canConfirm) return;
    onConfirm({ mode, value: num });
    setVal("");
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-[60]"
      onClick={onClose}
      data-testid="discount-modal"
    >
      <div className="bg-white rounded-md w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-heading text-xl font-semibold">Sconto</h3>
          <button onClick={onClose} data-testid="discount-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="text-xs font-mono uppercase tracking-widest text-velta-mute mb-4">
          {target?.label || "Applicato al conto"}
        </div>

        <div className="grid grid-cols-2 gap-2 mb-4">
          <button
            data-testid="discount-mode-pct"
            onClick={() => setMode("pct")}
            className={`h-12 rounded-md font-medium flex items-center justify-center gap-2 btn-tactile ${mode === "pct" ? "bg-velta-ink text-white" : "bg-white border border-velta-border text-velta-ink"}`}
          >
            <Percent className="w-4 h-4" /> Percentuale
          </button>
          <button
            data-testid="discount-mode-amount"
            onClick={() => setMode("amount")}
            className={`h-12 rounded-md font-medium flex items-center justify-center gap-2 btn-tactile ${mode === "amount" ? "bg-velta-ink text-white" : "bg-white border border-velta-border text-velta-ink"}`}
          >
            <Euro className="w-4 h-4" /> Importo €
          </button>
        </div>

        <div className="bg-velta-bg border-2 border-velta-border rounded-md p-4 mb-4">
          <div className="flex justify-between items-baseline mb-1">
            <span className="text-xs font-mono uppercase tracking-widest text-velta-mute">Sconto</span>
            <span data-testid="discount-input-display" className="font-mono font-bold text-3xl tabular-nums">
              {mode === "pct" ? `${num.toFixed(2)}%` : `€ ${num.toFixed(2)}`}
            </span>
          </div>
          <div className="flex justify-between items-baseline">
            <span className="text-xs font-mono uppercase tracking-widest text-velta-mute">Nuovo totale</span>
            <span data-testid="discount-preview" className="font-mono font-bold text-2xl tabular-nums text-velta-success">
              € {preview.toFixed(2)}
            </span>
          </div>
        </div>

        <NumericKeypad value={val} onChange={setVal} mode="currency" />

        <button
          data-testid="discount-confirm"
          onClick={submit}
          disabled={!canConfirm}
          className="w-full mt-5 h-14 bg-[#1E2D5B] hover:bg-[#162244] text-white rounded-md font-medium text-lg btn-tactile disabled:opacity-40"
        >
          Applica sconto
        </button>
      </div>
    </div>
  );
}
